/**
 * components/filterChip.tsx — selectable pill used by the Rules status filter,
 * the motion-family filter (with a leading dot), and the Builder tone selector.
 * Selected = ink fill + paper text (the design's `.sf-on` / `.mf-on` / `.tone-on`).
 */
import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { LightColors as C, FontSize as FS, FontWeight as FW } from '../theme/theme';
import { Txt } from './ui';

export interface FilterChipProps {
  label: string;
  selected: boolean;
  onPress: () => void;
  dotColor?: string;
  height?: number;
  paddingX?: number;
  /** 'solid' = ink-fill selected (primary filter). 'subtle' = soft-grey selected
   *  (a secondary filter, so two filter rows don't both shout in black). */
  variant?: 'solid' | 'subtle';
}

const FilterChip: React.FC<FilterChipProps> = ({
  label,
  selected,
  onPress,
  dotColor,
  height = 28,
  paddingX = 12,
  variant = 'solid',
}) => {
  const [hover, setHover] = useState(false);
  const subtle = variant === 'subtle';
  const bg = selected ? (subtle ? C.accentSoft : C.ink) : hover ? C.paper2 : C.card;
  const border = selected ? (subtle ? C.lineStrong : C.ink) : C.line;
  const fg = selected ? (subtle ? C.ink : C.paper) : C.ink2;
  const dotFill = selected ? (subtle ? dotColor ?? C.ink : C.paper) : dotColor;
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      style={{
        height,
        paddingHorizontal: paddingX,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: border,
        backgroundColor: bg,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {dotColor && <View style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: dotFill }} />}
      <Txt size={FS.sm} weight={selected && subtle ? FW.semibold : FW.medium} color={fg}>
        {label}
      </Txt>
    </Pressable>
  );
};

export default FilterChip;
